/**
 * SignUpWrapper - Handles VIP invite code routing for sign-up page
 *
 * Routing logic:
 * 1. VIP invite codes: Show sign-up flow to validate VIP access
 * 2. Everyone else: Redirect to quizlet flow
 */

import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useFeatureFlags } from '@/services/feature-flag-provider';
import { ROUTE_QUIZLET_VERSIONED } from '@/constants/routes';
import authService from '@/services/auth.service';
import { getPurchaseModel } from '@/hooks/use-purchase-model';
import Page from './sign-up.page';

/**
 * Check if an invite code is a VIP code that requires the sign-up flow
 * VIP codes start with "VIP_" or "VIP-" or end with "_VIP" or "-VIP" (case insensitive)
 */
function isVipInviteCode(code: string | null | undefined): boolean {
  if (!code) return false;
  const lowerCode = code.toLowerCase();
  return (
    lowerCode.startsWith('vip_') ||
    lowerCode.startsWith('vip-') ||
    lowerCode.endsWith('_vip') ||
    lowerCode.endsWith('-vip')
  );
}

export function SignUpWrapper() {
  const { isLoading } = useFeatureFlags();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Show blank screen while feature flags are loading to prevent flash
  if (isLoading) {
    return <div className="min-h-screen bg-white" />;
  }

  // Persist purchase model early — before any redirect strips ?pm from URL
  getPurchaseModel(searchParams);

  // Check for VIP invite code from URL or localStorage
  const urlInviteCode = searchParams.get('invite-code');
  const storedInviteCode = authService.getInviteCode();
  const hasVipCode = isVipInviteCode(urlInviteCode) || isVipInviteCode(storedInviteCode);

  // VIP users must go through sign-up flow to validate VIP access
  // and potentially bypass payment entirely
  if (hasVipCode) {
    return <Page />;
  }

  // Redirect everyone else to the quizlet flow
  return <Navigate to={`${ROUTE_QUIZLET_VERSIONED}${location.search}`} replace />;
}

export default SignUpWrapper;
